import { useState } from "react";
import { Image, Tooltip } from "antd";
import { CheckOutlined, DownOutlined, PlusOutlined } from "@ant-design/icons";
// @ts-ignore
import { useTranslation } from "react-i18next";
import { Procedure } from "../../../../types/procedures";
import { procedureDisplayName } from "../../../../utils/procedureDisplayName";
import { uploadedImageThumbPath, uploadedImagePath } from "../../../../utils/uploadedImage";
import { trackQuoteFocusBaClicked } from "../../../../utils/quoteAnalytics";
import { useIsMobile } from "../../MobileBreakpointContext";

export type SignatureCardVariant = "hero" | "row";

type SignatureProcedureCardProps = {
  procedure: Procedure;
  isSelected: boolean;
  isDisabled: boolean;
  variant: SignatureCardVariant;
  onSelect: () => void;
  onDeselect: () => void;
};

// Strip HTML tags (description can be rich text from Quill) and extract the
// first sentence as a plain-text tagline fallback.
function firstSentence(html: string | undefined | null): string | null {
  if (!html) return null;
  const plain = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const dot = plain.indexOf(".");
  if (dot > 0) return plain.slice(0, dot + 1);
  return plain.length > 0 ? plain : null;
}

// ◆ marker row — renders nothing for tier < 3 (low popularity is negative
// signaling, never shown).
const PopularityRow = ({
  popularity,
}: {
  popularity: { tier: number; count: number } | null | undefined;
}) => {
  // @ts-ignore
  const { t } = useTranslation("quote");
  if (!popularity || popularity.tier < 3) return null;

  const label =
    popularity.tier === 5
      ? t("procedures.popularityLabel5", {
          defaultValue: "Patient favorite · among our most requested",
        })
      : popularity.tier === 4
        ? t("procedures.popularityLabel4", { defaultValue: "Highly requested" })
        : null;

  return (
    <div className="sc-popularity">
      <div className="sc-popularity-markers">
        {[1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            className={i <= popularity.tier ? "sc-marker sc-marker--filled" : "sc-marker sc-marker--empty"}
          >
            ◆
          </span>
        ))}
      </div>
      {label && <span className="sc-popularity-label">{label}</span>}
    </div>
  );
};

// { label, value } strip — hidden when no facts. Max 3 per the plan.
// Exported (unchanged) so ProcedureStory can render the identical strip under
// its header instead of forking a second copy of the same markup + `sc-*` CSS.
type ProcedureFact = { label: string; labelEs?: string; value: string; valueEs?: string };

// The LanguageSwitcher sets regioned codes ("es-US"), so a strict === "es"
// never matches — normalize the way ProcedureStory does.
const isSpanish = (language?: string) => !!language && language.toLowerCase().includes("es");

export const FactsStrip = ({ facts, language }: { facts: ProcedureFact[] | null | undefined; language?: string }) => {
  if (!facts || facts.length === 0) return null;
  const es = isSpanish(language);
  return (
    <div className="sc-facts-strip">
      {facts.slice(0, 3).map((fact, i) => (
        <div key={i} className="sc-fact">
          <span className="sc-fact-label">
            {es && fact.labelEs ? fact.labelEs : fact.label}
          </span>
          <span className="sc-fact-value">
            {es && fact.valueEs ? fact.valueEs : fact.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// Before & afters section — tappable row opens PreviewGroup lightbox.
// Thumbs render with preview={false} so there's no antd mask overlay on each
// tile — the entire row is the tap target, which is cleaner on mobile.
const BeforeAfters = ({
  images,
  procedureId,
}: {
  images: string[];
  procedureId: string;
}) => {
  const [visible, setVisible] = useState(false);
  // @ts-ignore
  const { t } = useTranslation("quote");
  if (!images || images.length === 0) return null;

  const thumbs = images.slice(0, 3);

  const openLightbox = () => {
    trackQuoteFocusBaClicked({ procedureId, imageCount: images.length });
    setVisible(true);
  };

  return (
    <>
      {/* Hidden group driven by visible state */}
      <Image.PreviewGroup
        preview={{ visible, onVisibleChange: (v) => setVisible(v) }}
      >
        {images.map((key, i) => (
          <Image key={i} style={{ display: "none" }} src={uploadedImagePath(key)} />
        ))}
      </Image.PreviewGroup>

      {/* Tappable row — no individual preview overlays */}
      <button type="button" className="sc-ba-row" onClick={openLightbox}>
        <div className="sc-ba-thumbs">
          {thumbs.map((key, i) => (
            <img
              key={i}
              className="sc-ba-thumb"
              src={uploadedImageThumbPath(key, 120)}
              alt=""
            />
          ))}
        </div>
        <span className="sc-ba-label">
          {t("procedures.seeBeforeAfters", {
            defaultValue: "See real before & afters →",
          })}
        </span>
      </button>
    </>
  );
};

// ── Hero card (single-focus) ──────────────────────────────────────────────────
const SignatureProcedureHero = ({
  procedure,
  isSelected,
  isDisabled,
  onSelect,
  onDeselect,
}: Omit<SignatureProcedureCardProps, "variant">) => {
  // @ts-ignore
  const { t, i18n } = useTranslation("quote");
  // @ts-ignore
  const { t: tProcedures } = useTranslation("procedures");
  const isMobile = useIsMobile();
  const language = i18n?.language;
  const tagline =
    (isSpanish(language) ? procedure.taglineEs : null) ||
    procedure.tagline ||
    firstSentence(procedure.description);
  const subtitle =
    (isSpanish(language) ? procedure.subtitleEs : null) || procedure.subtitle;

  // heroImage > regular image (heroImage is the curated editorial portrait;
  // image is the catalog thumbnail — often the same until Phase 2 curation)
  const photoKey = procedure.heroImage || procedure.image || procedure.maleImage;
  const imageUrl = photoKey ? uploadedImageThumbPath(photoKey, 800) : undefined;

  const facts = procedure.facts || [];
  const baImages = procedure.beforeAfterImages || [];

  const selectButton = isSelected ? (
    <button
      className="sc-select-btn sc-select-btn--selected"
      onClick={isDisabled ? undefined : onDeselect}
      disabled={isDisabled}
      type="button"
    >
      <CheckOutlined />
      <span>
        {t("procedures.addedToQuote", { defaultValue: "Added to your quote" })}
      </span>
      <span className="sc-select-change">
        {t("procedures.change", { defaultValue: "change" })}
      </span>
    </button>
  ) : (
    <button
      className="sc-select-btn"
      onClick={isDisabled ? undefined : onSelect}
      disabled={isDisabled}
      type="button"
    >
      <PlusOutlined />
      <span>{t("procedures.addToQuote", { defaultValue: "Add to quote" })}</span>
    </button>
  );

  return (
    <div
      className={[
        "sc-hero",
        isSelected ? "sc-hero--selected" : "",
        isDisabled ? "sc-hero--disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Eyebrow */}
      <div className="sc-eyebrow">
        ◆ {t("procedures.featuredProcedure", { defaultValue: "FEATURED PROCEDURE" })}
      </div>

      {/* Content row: left text / right photo */}
      <div className="sc-hero-content">
        <div className="sc-hero-text">
          <h2 className="sc-title">{procedureDisplayName(procedure, { language, t: tProcedures })}</h2>
          {subtitle && <div className="sc-subtitle">{subtitle}</div>}
          {tagline && <p className="sc-tagline">{tagline}</p>}
          <PopularityRow popularity={procedure.popularity} />
          <FactsStrip facts={facts.length > 0 ? facts : null} language={language} />
        </div>

        <div className="sc-hero-photo-col">
          {imageUrl && (
            <div className="sc-photo-arch">
              <img src={imageUrl} alt={procedureDisplayName(procedure, { language, t: tProcedures })} className="sc-photo-img" />
            </div>
          )}
          {baImages.length > 0 && !isMobile && (
            <BeforeAfters images={baImages} procedureId={procedure.id} />
          )}
        </div>
      </div>

      {/* Selection button */}
      <div className="sc-hero-footer">
        {isDisabled ? (
          <Tooltip title={t("procedures.incompatibilityTooltip")}>
            {selectButton}
          </Tooltip>
        ) : (
          selectButton
        )}
      </div>

      {/* B&A mobile — below footer so it doesn't compress the photo column */}
      {baImages.length > 0 && isMobile && (
        <BeforeAfters images={baImages} procedureId={procedure.id} />
      )}
    </div>
  );
};

// ── Compact row (multi-focus / family) ────────────────────────────────────────
const SignatureProcedureRow = ({
  procedure,
  isSelected,
  isDisabled,
  onSelect,
  onDeselect,
}: Omit<SignatureProcedureCardProps, "variant">) => {
  const [expanded, setExpanded] = useState(false);
  const [baVisible, setBaVisible] = useState(false);
  // @ts-ignore
  const { t, i18n } = useTranslation("quote");
  // @ts-ignore
  const { t: tProcedures } = useTranslation("procedures");
  const language = i18n?.language;
  const tagline =
    (isSpanish(language) ? procedure.taglineEs : null) ||
    procedure.tagline ||
    firstSentence(procedure.description);
  const facts = procedure.facts || [];
  const baImages = procedure.beforeAfterImages || [];

  const photoKey = procedure.heroImage || procedure.image || procedure.maleImage;
  const imageUrl = photoKey ? uploadedImageThumbPath(photoKey, 240) : undefined;

  const onToggle = () => {
    if (!isDisabled) {
      isSelected ? onDeselect() : onSelect();
    }
  };

  const selectBtn = (
    <button
      type="button"
      className={[
        "sc-row-select",
        isSelected ? "sc-row-select--selected" : "",
        isDisabled ? "sc-row-select--disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      disabled={isDisabled}
    >
      {isSelected ? <CheckOutlined /> : <PlusOutlined />}
    </button>
  );

  // A tap anywhere on the row TOGGLES SELECTION, the same as every other card
  // in the funnel. It used to only expand/collapse, with selection hidden on a
  // 36px side button — so the row read as "tap to add" and silently did
  // nothing of the sort (2026-09-13 audit). Expanding now has its own explicit
  // control below, which stopPropagation()s out of this handler.
  return (
    <div
      className={[
        "sc-row",
        isSelected ? "sc-row--selected" : "",
        isDisabled ? "sc-row--disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={onToggle}
      role="button"
      aria-pressed={isSelected}
      aria-disabled={isDisabled}
      tabIndex={0}
      onKeyDown={(e) => {
        // Only when the ROW itself holds focus. The nested buttons (select,
        // details toggle, before & afters) stopPropagation() on CLICK, but a
        // keydown still bubbles here — so without this guard Enter/Space on the
        // select button toggled twice (native activation + this handler, i.e.
        // visibly nothing), and Enter/Space on the other two silently changed
        // the selection as a side effect. The unconditional preventDefault()
        // below also used to swallow Space's native activation of those
        // buttons. Comparing against currentTarget covers every nested
        // interactive element, including ones added later.
        if (e.target !== e.currentTarget) return;
        if (e.key !== "Enter" && e.key !== " ") return;
        // Space would otherwise scroll the page under the row.
        e.preventDefault();
        onToggle();
      }}
    >
      {/* Left: photo */}
      {imageUrl && (
        <div className="sc-row-photo-wrap">
          <img src={imageUrl} alt={procedureDisplayName(procedure, { language, t: tProcedures })} className="sc-row-photo" />
        </div>
      )}

      {/* Middle: name + details */}
      <div className="sc-row-body">
        <div className="sc-row-name">{procedureDisplayName(procedure, { language, t: tProcedures })}</div>
        <PopularityRow popularity={procedure.popularity} />
        {baImages.length > 0 && (
          <>
            {/* Hidden PreviewGroup — triggered programmatically by the link */}
            <Image.PreviewGroup
              preview={{
                visible: baVisible,
                onVisibleChange: (v) => setBaVisible(v),
              }}
            >
              {baImages.map((key, i) => (
                <Image
                  key={i}
                  style={{ display: "none" }}
                  src={uploadedImagePath(key)}
                />
              ))}
            </Image.PreviewGroup>
            <button
              type="button"
              className="sc-row-ba-link"
              onClick={(e) => {
                e.stopPropagation();
                trackQuoteFocusBaClicked({
                  procedureId: procedure.id,
                  imageCount: baImages.length,
                });
                setBaVisible(true);
              }}
            >
              {t("procedures.seeBeforeAfters", {
                defaultValue: "Before & afters →",
              })}
            </button>
          </>
        )}

        {/* Explicit expand/collapse control — the row itself now selects, so
            details need their own affordance. */}
        <button
          type="button"
          className="sc-row-details-toggle"
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((x) => !x);
          }}
        >
          {expanded
            ? t("procedures.rowDetailsHide", { defaultValue: "Hide details" })
            : t("procedures.rowDetailsShow", { defaultValue: "Show details" })}
          <DownOutlined
            className={
              expanded
                ? "sc-row-details-chevron is-open"
                : "sc-row-details-chevron"
            }
          />
        </button>

        {/* Expanded detail: tagline + facts */}
        {expanded && (
          <div className="sc-row-detail">
            {tagline && <p className="sc-tagline">{tagline}</p>}
            <FactsStrip facts={facts.length > 0 ? facts : null} language={language} />
          </div>
        )}
      </div>

      {/* Right: select toggle */}
      <div className="sc-row-action">
        {isDisabled ? (
          <Tooltip title={t("procedures.incompatibilityTooltip")}>
            {selectBtn}
          </Tooltip>
        ) : (
          selectBtn
        )}
      </div>
    </div>
  );
};

// ── Public export ─────────────────────────────────────────────────────────────
const SignatureProcedureCard = (props: SignatureProcedureCardProps) =>
  props.variant === "hero" ? (
    <SignatureProcedureHero {...props} />
  ) : (
    <SignatureProcedureRow {...props} />
  );

export default SignatureProcedureCard;
